/**
 * Seep — server-authoritative rules engine (North Indian capture game, four players in two partnerships).
 *
 * Rules implemented (the table settings live in {@link SeepConfig}):
 *  - One 52-card deck. Card values: ace 1, 2–10 face value, jack 11, queen 12, king 13. Seats 0 & 2 (team 0) play
 *    seats 1 & 3 (team 1). Four cards are laid face up on the floor, then four are dealt to each player; when every
 *    hand is empty four more are dealt, until the deck is gone (twelve cards each in all).
 *  - On your turn you play one card and do exactly one of three things:
 *      TRAIL    – leave it on the floor. Not allowed while you own a house.
 *      CAPTURE  – take floor cards with it: a set of loose cards whose values add up to the played card (a single
 *                 equal card counts), plus every house of that value (a house is always taken whole, and a house
 *                 of the played value is taken even when no loose card is). The played card goes with the haul.
 *      BUILD    – make a house: pick loose cards that, with the played card, add up to 9–13, and name that value.
 *                 The cards stay on the floor tied together as one house owned by you. You must hold another card of
 *                 that value, and you may only play a card that would leave you without one by capturing the house.
 *                 A team can own one house at a time, and no two houses may share a value. A house that has not been
 *                 increased can instead be increased by adding a card to it (any player may do this, to a house of
 *                 either team): its value rises by the card's value, up to 13, and it becomes yours — a house can be
 *                 increased only once.
 *  - Clearing the floor with a capture is a seep, worth 50 points — except on the last play of the deal. Whoever
 *    captured last takes the cards left on the floor at the end.
 *  - Scoring per deal: every spade is worth its face value (ace 1 … king 13, 91 in all), each other ace 1, and the 10
 *    of diamonds 6 — 100 points in the pack — plus 50 for each seep. The match goes to `targetScore` (default 100),
 *    continuing past a tie. Partners share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface SeepConfig {
  targetScore: number;
  /** Hard cap on deals so a match always terminates. */
  maxRounds: number;
  roundEndDelayMs: number;
  moveDelayMs: number;
}

export type SeepPhase = 'PLAYING' | 'ROUND_END' | 'MATCH_END';

export interface SeepHouse {
  id: number;
  cards: CardId[];
  value: number;
  owner: number;
  increased: boolean;
}

export type SeepMode = 'trail' | 'capture' | 'build';

export interface SeepMove {
  seat: number;
  card: CardId;
  mode: SeepMode;
  /** Loose cards taken (capture) or tied into the house (build). */
  taken: CardId[];
  /** Value of every house taken (capture), or of the house built or increased (build). */
  houseValue: number | null;
  housesTaken: number;
  seep: boolean;
}

export interface SeepTeamRound {
  cards: number;
  /** Points in the cards captured. */
  cardPoints: number;
  seeps: number;
  total: number;
}

export interface SeepState {
  config: SeepConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: SeepPhase;
  deck: CardId[];
  hands: CardId[][];
  loose: CardId[];
  houses: SeepHouse[];
  nextHouseId: number;
  /** Cards captured so far this deal, per team. */
  captured: CardId[][];
  seeps: number[];
  lastCapturer: number | null;
  turn: number;
  lastMove: SeepMove | null;
  teamScores: number[];
  roundResults: SeepTeamRound[][];
}

export interface SeepAction {
  type: 'play';
  card: CardId;
  mode: SeepMode;
  /** Loose floor cards used by a capture or a new house. */
  take: CardId[];
  /** The house to increase (build only), else null. */
  target: number | null;
  /** The value of the house being built or increased (build only), else 0. */
  value: number;
}

export interface SeepBuildOption {
  take: CardId[];
  target: number | null;
  value: number;
}

export interface SeepLegal {
  moves: { card: CardId; trail: boolean; captures: CardId[][]; builds: SeepBuildOption[] }[];
}

export interface SeepView {
  gameType: 'SEEP';
  config: SeepConfig;
  seat: number | null;
  phase: SeepPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  loose: CardId[];
  houses: SeepHouse[];
  deckCount: number;
  capturedCounts: number[];
  seeps: number[];
  lastMove: SeepMove | null;
  teamScores: number[];
  roundResults: SeepTeamRound[][];
  totals: number[];
}

const DEFAULTS: SeepConfig = {
  targetScore: 100,
  maxRounds: 10,
  roundEndDelayMs: 7000,
  moveDelayMs: 0,
};

const SEATS = 4;
const SEEP_BONUS = 50;
const MIN_HOUSE = 9;
const MAX_HOUSE = 13;

export const seepTeamOf = (seat: number): number => seat % 2;
export const seepPartnerOf = (seat: number): number => (seat + 2) % SEATS;

/** Card value: ace 1, 2–10 face value, jack 11, queen 12, king 13. */
export const seepValue = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r;
};

/** Points a captured card is worth: spades by face value, other aces 1, the 10 of diamonds 6. */
export const seepPoints = (card: CardId): number => {
  const { rank, suit } = parseCard(card);
  if (suit === 'S') return rank === 14 ? 1 : rank;
  if (rank === 14) return 1;
  if (rank === 10 && suit === 'D') return 6;
  return 0;
};

export const seepPointsOf = (cards: readonly CardId[]): number => cards.reduce((n, c) => n + seepPoints(c), 0);

/** Every subset of `cards` whose values add up to `target`. */
export function seepSumSets(cards: readonly CardId[], target: number): CardId[][] {
  const out: CardId[][] = [];
  const vals = cards.map(seepValue);
  const rec = (start: number, sum: number, picked: CardId[]) => {
    if (sum === target) {
      out.push([...picked]);
      return;
    }
    for (let i = start; i < cards.length; i++) {
      if (sum + vals[i]! > target) continue;
      picked.push(cards[i]!);
      rec(i + 1, sum + vals[i]!, picked);
      picked.pop();
    }
  };
  rec(0, 0, []);
  return out;
}

/** The part of the state that decides which plays are legal. */
export interface SeepFloor {
  loose: readonly CardId[];
  houses: readonly SeepHouse[];
}

export type SeepPlan =
  | { mode: 'trail' }
  | { mode: 'capture'; taken: CardId[]; houses: SeepHouse[] }
  | { mode: 'build'; taken: CardId[]; target: SeepHouse | null; value: number };

/** Checks a play against the floor and returns what it does, or the reason it is illegal. */
export function seepCheck(floor: SeepFloor, hand: readonly CardId[], seat: number, action: SeepAction): SeepPlan | string {
  const ci = hand.indexOf(action.card);
  if (ci < 0) return 'Card not in hand';
  const rest = hand.filter((_, i) => i !== ci);
  const v = seepValue(action.card);
  const owned = floor.houses.find((h) => h.owner === seat);
  const take = action.take;
  if (new Set(take).size !== take.length) return 'Duplicate cards in the play';
  if (take.some((c) => !floor.loose.includes(c))) return 'Those cards are not on the floor';
  const holds = (value: number) => rest.some((c) => seepValue(c) === value);

  if (action.mode === 'trail') {
    if (take.length > 0 || action.target !== null) return 'A trailed card takes nothing';
    if (owned) return 'You must play to your house while you own one';
    return { mode: 'trail' };
  }

  if (action.mode === 'capture') {
    if (action.target !== null) return 'A capture does not name a house';
    if (take.reduce((n, c) => n + seepValue(c), 0) !== (take.length > 0 ? v : 0)) return 'Those cards do not add up to the card played';
    const houses = floor.houses.filter((h) => h.value === v);
    if (take.length === 0 && houses.length === 0) return 'There is nothing to capture with that card';
    if (owned && !houses.includes(owned) && !holds(owned.value)) return 'You must keep a card for your house';
    return { mode: 'capture', taken: [...take], houses };
  }

  // Build: a new house from loose cards, or an increase of an existing one.
  const team = seepTeamOf(seat);
  if (action.target !== null) {
    const house = floor.houses.find((h) => h.id === action.target);
    if (!house) return 'No such house';
    if (house.increased) return 'That house has already been increased';
    if (take.length > 0) return 'An increase uses only the card played';
    const value = house.value + v;
    if (value > MAX_HOUSE) return 'A house cannot be worth more than 13';
    if (action.value !== value) return 'Wrong house value';
    if (floor.houses.some((h) => h !== house && (seepTeamOf(h.owner) === team || h.value === value))) return 'That would leave two houses';
    if (!holds(value)) return 'You need a card of the house value';
    return { mode: 'build', taken: [], target: house, value };
  }
  if (take.length === 0) return 'Pick the cards to build with';
  const value = v + take.reduce((n, c) => n + seepValue(c), 0);
  if (value < MIN_HOUSE || value > MAX_HOUSE) return 'A house is worth 9 to 13';
  if (action.value !== value) return 'Wrong house value';
  if (floor.houses.some((h) => seepTeamOf(h.owner) === team)) return 'Your side already has a house';
  if (floor.houses.some((h) => h.value === value)) return 'There is already a house of that value';
  if (!holds(value)) return 'You need a card of the house value';
  return { mode: 'build', taken: [...take], target: null, value };
}

const trailAction = (card: CardId): SeepAction => ({ type: 'play', card, mode: 'trail', take: [], target: null, value: 0 });

/** Every legal play of a hand against the floor. */
export function seepLegal(floor: SeepFloor, hand: readonly CardId[], seat: number): SeepLegal {
  const moves: SeepLegal['moves'] = [];
  for (const card of new Set(hand)) {
    const v = seepValue(card);
    const ok = (a: SeepAction) => typeof seepCheck(floor, hand, seat, a) !== 'string';
    const captures: CardId[][] = [];
    const groups = seepSumSets(floor.loose, v);
    if (floor.houses.some((h) => h.value === v)) groups.unshift([]);
    for (const g of groups) if (ok({ type: 'play', card, mode: 'capture', take: g, target: null, value: 0 })) captures.push(g);
    const builds: SeepBuildOption[] = [];
    for (let value = Math.max(MIN_HOUSE, v + 1); value <= MAX_HOUSE; value++) {
      for (const g of seepSumSets(floor.loose, value - v)) {
        if (ok({ type: 'play', card, mode: 'build', take: g, target: null, value })) builds.push({ take: g, target: null, value });
      }
    }
    for (const h of floor.houses) {
      const value = h.value + v;
      if (ok({ type: 'play', card, mode: 'build', take: [], target: h.id, value })) builds.push({ take: [], target: h.id, value });
    }
    moves.push({ card, trail: ok(trailAction(card)), captures, builds });
  }
  return { moves };
}

function dealHands(state: SeepState): void {
  for (let s = 0; s < SEATS; s++) {
    const next = state.deck.splice(0, 4);
    state.hands[s] = sortHand([...(state.hands[s] ?? []), ...next]);
  }
}

function startRound(state: SeepState, rng: Rng, events: EngineEvent[]): void {
  state.deck = shuffle(buildDeck(), rng);
  state.loose = state.deck.splice(0, 4);
  state.houses = [];
  state.hands = [[], [], [], []];
  dealHands(state);
  state.captured = [[], []];
  state.seeps = [0, 0];
  state.lastCapturer = null;
  state.lastMove = null;
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, loose: [...state.loose] });
}

/** Scores a deal for both teams. */
export function seepScoreRound(captured: readonly (readonly CardId[])[], seeps: readonly number[]): SeepTeamRound[] {
  return [0, 1].map((t) => {
    const cardPoints = seepPointsOf(captured[t]!);
    return { cards: captured[t]!.length, cardPoints, seeps: seeps[t]!, total: cardPoints + SEEP_BONUS * seeps[t]! };
  });
}

export const seepEngine: GameEngine<SeepConfig, SeepState, SeepAction, SeepView, SeepLegal> = {
  gameType: 'SEEP',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 20, 500);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('moveDelayMs', c.moveDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Seep needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: SeepState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      deck: [],
      hands: [],
      loose: [],
      houses: [],
      nextHouseId: 1,
      captured: [[], []],
      seeps: [0, 0],
      lastCapturer: null,
      turn: 0,
      lastMove: null,
      teamScores: [0, 0],
      roundResults: [],
    };
    startRound(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw) || raw.type !== 'play' || typeof raw.card !== 'string' || raw.card.length > 4) {
      throw new EngineError('INVALID_ACTION', 'Unknown Seep action');
    }
    const mode = raw.mode;
    if (mode !== 'trail' && mode !== 'capture' && mode !== 'build') throw new EngineError('INVALID_ACTION', 'Unknown Seep action');
    const take = raw.take === undefined ? [] : raw.take;
    if (!Array.isArray(take) || take.length > 14 || !take.every((c) => typeof c === 'string' && c.length <= 4)) {
      throw new EngineError('INVALID_ACTION', 'Bad Seep cards');
    }
    const target = raw.target === undefined || raw.target === null ? null : raw.target;
    if (target !== null && (typeof target !== 'number' || !Number.isInteger(target))) throw new EngineError('INVALID_ACTION', 'Bad house');
    const value = raw.value === undefined ? 0 : raw.value;
    if (typeof value !== 'number' || !Number.isInteger(value)) throw new EngineError('INVALID_ACTION', 'Bad house value');
    return { type: 'play', card: raw.card, mode, take: take as string[], target: target as number | null, value };
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase === 'PLAYING', 'GAME_OVER', 'No move is expected now');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    const plan = seepCheck(state, hand, seat, action);
    if (typeof plan === 'string') throw new EngineError('ILLEGAL_MOVE', plan);

    const events: EngineEvent[] = [];
    const team = seepTeamOf(seat);
    removeCards(hand, [action.card]);
    let seep = false;
    let taken: CardId[] = [];
    let houseValue: number | null = null;
    let housesTaken = 0;
    if (plan.mode === 'trail') {
      state.loose.push(action.card);
    } else if (plan.mode === 'capture') {
      taken = plan.taken;
      removeCards(state.loose, taken);
      const gone = new Set(plan.houses.map((h) => h.id));
      state.houses = state.houses.filter((h) => !gone.has(h.id));
      state.captured[team]!.push(action.card, ...taken, ...plan.houses.flatMap((h) => h.cards));
      state.lastCapturer = seat;
      housesTaken = plan.houses.length;
      houseValue = plan.houses[0]?.value ?? null;
      const lastPlay = state.deck.length === 0 && state.hands.every((h) => h.length === 0);
      if (state.loose.length === 0 && state.houses.length === 0 && !lastPlay) {
        seep = true;
        state.seeps[team]!++;
      }
    } else {
      houseValue = plan.value;
      if (plan.target) {
        plan.target.cards.push(action.card);
        plan.target.value = plan.value;
        plan.target.owner = seat;
        plan.target.increased = true;
      } else {
        taken = plan.taken;
        removeCards(state.loose, taken);
        state.houses.push({ id: state.nextHouseId++, cards: [action.card, ...taken], value: plan.value, owner: seat, increased: false });
      }
    }
    state.lastMove = { seat, card: action.card, mode: plan.mode, taken: [...taken], houseValue, housesTaken, seep };
    events.push({ type: plan.mode, seat, card: action.card, taken: [...taken], houseValue, housesTaken, seep });

    if (state.hands.every((h) => h.length === 0)) {
      if (state.deck.length > 0) {
        dealHands(state);
        events.push({ type: 'dealMore', counts: state.hands.map((h) => h.length), deck: state.deck.length });
      } else {
        endRound(state, events);
        return events;
      }
    }
    state.turn = (seat + 1) % SEATS;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { moves: [] };
    return seepLegal(state, state.hands[seat]!, seat);
  },

  getTimeoutAction(state, seat) {
    return seepSafeAction(seepLegal(state, state.hands[seat]!, seat));
  },

  getView(state, seat) {
    return {
      gameType: 'SEEP',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      loose: [...state.loose],
      houses: state.houses.map((h) => ({ ...h, cards: [...h.cards] })),
      deckCount: state.deck.length,
      capturedCounts: state.captured.map((c) => c.length),
      seeps: [...state.seeps],
      lastMove: state.lastMove ? { ...state.lastMove, taken: [...state.lastMove.taken] } : null,
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[seepTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    return state.phase === 'ROUND_END' ? { kind: 'nextRound', delayMs: state.config.roundEndDelayMs } : null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      startRound(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/** A safe play for a timeout: the richest capture, else the cheapest trail, else any legal play. */
export function seepSafeAction(legal: SeepLegal): SeepAction {
  let best: { card: CardId; take: CardId[]; score: number } | null = null;
  for (const m of legal.moves) {
    for (const g of m.captures) {
      const score = seepPoints(m.card) + seepPointsOf(g);
      if (!best || score > best.score) best = { card: m.card, take: g, score };
    }
  }
  if (best) return { type: 'play', card: best.card, mode: 'capture', take: best.take, target: null, value: 0 };
  const trails = legal.moves.filter((m) => m.trail).sort((a, b) => seepPoints(a.card) - seepPoints(b.card));
  if (trails.length > 0) return trailAction(trails[0]!.card);
  const m = legal.moves.find((x) => x.builds.length > 0);
  if (!m) throw new EngineError('ILLEGAL_MOVE', 'No legal play');
  const b = m.builds[0]!;
  return { type: 'play', card: m.card, mode: 'build', take: b.take, target: b.target, value: b.value };
}

function endRound(state: SeepState, events: EngineEvent[]): void {
  // The last capturer's side takes whatever is left on the floor.
  const left = [...state.loose, ...state.houses.flatMap((h) => h.cards)];
  if (left.length > 0 && state.lastCapturer !== null) {
    state.captured[seepTeamOf(state.lastCapturer)]!.push(...left);
    events.push({ type: 'floorSwept', seat: state.lastCapturer, count: left.length });
    state.loose = [];
    state.houses = [];
  }
  const result = seepScoreRound(state.captured, state.seeps);
  result.forEach((r, t) => {
    state.teamScores[t]! += r.total;
  });
  state.roundResults.push(result);
  events.push({ type: 'roundEnd', round: state.round, results: result, teamScores: [...state.teamScores] });
  const [a, b] = state.teamScores as [number, number];
  const over = (Math.max(a, b) >= state.config.targetScore && a !== b) || state.round >= state.config.maxRounds;
  if (over) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standings(state) });
  } else {
    state.phase = 'ROUND_END';
  }
}

/** Partners share a place: the higher score is 1st (both 1st on a tie). */
function standings(state: SeepState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const mine = seepTeamOf(seat) === 0 ? a : b;
    const theirs = seepTeamOf(seat) === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
